import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Clock, Truck, Handshake, FileText, Rss,
  ArrowUpRight, MessageSquare, Loader2
} from 'lucide-react';
import { useAdmin } from './AdminContext';

export default function DashboardPage() {
  const { token, adminName, apiUrl, logout } = useAdmin();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentSubs, setRecentSubs] = useState([]);
  const [postCounts, setPostCounts] = useState({ manual: 0, rss: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, postsRes, subsRes] = await Promise.all([
          fetch(`${apiUrl}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiUrl}/api/blog/posts?published_only=false`),
          fetch(`${apiUrl}/api/contact/submissions`),
        ]);
        if (statsRes.status === 401) { logout(); return; }
        const statsData = await statsRes.json();
        const postsData = await postsRes.json();
        const subsData = await subsRes.json();
        setStats(statsData);
        setRecentSubs(subsData.slice(0, 5));
        setPostCounts({
          manual: postsData.filter(p => p.source !== 'rss').length,
          rss: postsData.filter(p => p.source === 'rss').length,
        });
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiUrl, token, logout]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#4fd1c5] animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Leads', value: stats?.total_submissions || 0, icon: Users, accent: '#3b82f6', bg: 'from-blue-500/15 to-blue-600/5', border: 'border-blue-500/15' },
    { label: 'New Leads', value: stats?.new_submissions || 0, icon: Clock, accent: '#f59e0b', bg: 'from-amber-500/15 to-amber-600/5', border: 'border-amber-500/15' },
    { label: 'Pickup Requests', value: stats?.pickup_count || 0, icon: Truck, accent: '#10b981', bg: 'from-emerald-500/15 to-emerald-600/5', border: 'border-emerald-500/15' },
    { label: 'Partner Inquiries', value: stats?.partner_count || 0, icon: Handshake, accent: '#8b5cf6', bg: 'from-violet-500/15 to-violet-600/5', border: 'border-violet-500/15' },
  ];

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-white">Welcome back, {adminName}</h2>
        <p className="text-gray-500 text-sm mt-0.5">Here's what's happening with Grenew today.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-stats">
        {statCards.map((s, i) => (
          <div
            key={i}
            className={`bg-gradient-to-br ${s.bg} border ${s.border} rounded-xl p-4 lg:p-5 transition-all hover:scale-[1.02] hover:shadow-lg`}
            style={{ '--card-accent': s.accent }}
          >
            <div className="flex items-center justify-between mb-3">
              <s.icon className="w-5 h-5" style={{ color: s.accent }} />
              <ArrowUpRight className="w-3.5 h-3.5 text-gray-600" />
            </div>
            <div className="text-2xl lg:text-3xl font-bold text-white">{s.value}</div>
            <div className="text-[11px] text-gray-400 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Content + Recent Leads grid */}
      <div className="grid lg:grid-cols-5 gap-4">
        {/* Content Overview */}
        <div className="lg:col-span-2 bg-white/[0.03] border border-white/[0.06] rounded-xl p-5">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-white font-semibold text-sm">Content Overview</h3>
            <FileText className="w-4 h-4 text-[#4fd1c5]" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-white/[0.03] rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#4fd1c5]/10 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-[#4fd1c5]" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{postCounts.manual}</p>
                  <p className="text-gray-500 text-[11px]">Manual Posts</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/admin/posts')}
                className="text-[#4fd1c5] text-xs font-medium hover:underline"
              >
                Manage
              </button>
            </div>
            <div className="flex items-center justify-between bg-white/[0.03] rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Rss className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{postCounts.rss}</p>
                  <p className="text-gray-500 text-[11px]">RSS Articles</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/admin/news')}
                className="text-[#4fd1c5] text-xs font-medium hover:underline"
              >
                View
              </button>
            </div>
          </div>
        </div>

        {/* Recent Leads */}
        <div className="lg:col-span-3 bg-white/[0.03] border border-white/[0.06] rounded-xl p-5">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-white font-semibold text-sm">Recent Leads</h3>
            <button
              onClick={() => navigate('/admin/submissions')}
              className="text-[#4fd1c5] text-xs font-medium hover:underline flex items-center gap-1"
            >
              View all <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            {recentSubs.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                <p className="text-gray-500 text-xs">No submissions yet</p>
              </div>
            ) : (
              recentSubs.map((sub) => (
                <div
                  key={sub.id}
                  className="flex items-center gap-3 bg-white/[0.02] hover:bg-white/[0.04] rounded-lg px-4 py-3 transition-colors"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    sub.type === 'pickup' ? 'bg-blue-500/15' : 'bg-violet-500/15'
                  }`}>
                    {sub.type === 'pickup'
                      ? <Truck className="w-4 h-4 text-blue-400" />
                      : <Handshake className="w-4 h-4 text-violet-400" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-medium truncate">{sub.name}</p>
                    <p className="text-gray-500 text-[10px] truncate">{sub.company}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-2 py-0.5 text-[9px] font-bold rounded-full uppercase ${
                      sub.status === 'new' ? 'bg-amber-500/15 text-amber-400' :
                      sub.status === 'in_progress' ? 'bg-blue-500/15 text-blue-400' :
                      'bg-green-500/15 text-green-400'
                    }`}>
                      {sub.status?.replace('_', ' ')}
                    </span>
                    <span className="text-gray-600 text-[10px] hidden sm:block">{formatDate(sub.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
